import { Component, inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';

import { NotificationActions } from 'src/app/store/application/notification/notification.actions';
import {
    selectSelectedNotification,
    selectIsSelectedUnread,
    selectShowNotificationDialog,
} from 'src/app/store/application/notification/notification.selectors';
import { selectSelectedCompanyId } from 'src/app/store/user/user.selectors';
import { NotificationType, NotificationTag } from 'src/api-service';

@Component({
    selector: 'app-notification-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        TranslateModule,
        DialogModule,
        ButtonModule,
        TagModule,
    ],
    templateUrl: './notification-dialog.component.html',
    styleUrl: './notification-dialog.component.scss',
})
export class NotificationDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly sanitizer = inject(DomSanitizer);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly notification = toSignal(this.store.select(selectSelectedNotification));
    public readonly isUnread = toSignal(this.store.select(selectIsSelectedUnread), { initialValue: false });
    public readonly showDialog = toSignal(this.store.select(selectShowNotificationDialog), { initialValue: false });
    private readonly companyId = toSignal(this.store.select(selectSelectedCompanyId));

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly dialogHeader = computed(() => {
        const notification = this.notification();
        if (!notification) return '';
        const icon = this.getTypeIcon(notification.type);
        return `${icon} ${notification.title}`;
    });

    public readonly processedContent = computed((): SafeHtml => {
        const notification = this.notification();
        if (!notification?.dialogPopupContentHtml) return '';
        return this.sanitizer.bypassSecurityTrustHtml(notification.dialogPopupContentHtml);
    });

    /**
     * Forced actions (notifications that pop up on their own at start) must be
     * acknowledged - no close icon, no mask click, no escape.
     */
    public readonly isForced = computed(() => this.notification()?.dialogPopupAtStart ?? false);

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public getTypeIcon(type: NotificationType | undefined): string {
        switch (type) {
            case NotificationType.Release: return '🚀';
            case NotificationType.Hotfix: return '🔧';
            case NotificationType.Info: return 'ℹ️';
            case NotificationType.Maintenance: return '🔨';
            case NotificationType.Warning: return '⚠️';
            default: return '📢';
        }
    }

    public getTagLabel(tag: NotificationTag): string {
        const tagString = String(tag).toUpperCase().replace(/ /g, '_');
        const key = `NOTIFICATION.TAG.${tagString}`;
        const translated = this.translate.instant(key);
        return translated !== key ? translated : String(tag);
    }

    public getTagSeverity(tag: NotificationTag): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        switch (tag) {
            case NotificationTag.Breaking: return 'danger';
            case NotificationTag.NewFeature: return 'success';
            case NotificationTag.Improvement: return 'info';
            case NotificationTag.Bugfix: return 'warn';
            case NotificationTag.Security: return 'danger';
            case NotificationTag.Performance: return 'info';
            case NotificationTag.Deprecated: return 'secondary';
            case NotificationTag.Urgent: return 'danger';
            case NotificationTag.ActionRequired: return 'warn';
            default: return 'secondary';
        }
    }

    public onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.store.dispatch(NotificationActions.clearSelection());
        }
    }

    public close(): void {
        this.store.dispatch(NotificationActions.clearSelection());
    }

    public markAsRead(id: number): void {
        this.store.dispatch(NotificationActions.markAsRead({ id }));
    }

    public navigateToAction(url: string, notificationId: number): void {
        // Mark as read first, then navigate
        this.store.dispatch(NotificationActions.markAsRead({ id: notificationId }));

        // Check if it's an external URL or internal route
        if (url.startsWith('http://') || url.startsWith('https://')) {
            window.open(url, '_blank');
        } else {
            this.router.navigateByUrl(url);
        }
    }

    public navigateToDetails(notificationId: number): void {
        const companyId = this.companyId();
        if (companyId) {
            this.store.dispatch(NotificationActions.markAsRead({ id: notificationId }));
            this.store.dispatch(NotificationActions.clearSelection());
            this.router.navigate(['/', companyId, 'home', 'release-notes', notificationId]);
        }
    }
}
